"use client";
import React from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Link from "next/link";
import PageTransition from "@/components/animations/PageTransition";
import FadeInView from "@/components/animations/FadeInView";
import StaggerContainer from "@/components/animations/StaggerContainer";
import Breadcrumb from "@/components/Breadcrumb";

const challenges = [
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <path d="M2 12h20" />
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      </svg>
    ),
    title: "Distributed Office Locations",
    desc: "Consistent technology across multiple office locations — same systems, same security, same performance for every team.",
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 6v6l4 2" />
        <path d="M16.24 7.76a6 6 0 1 0 .01 8.49" />
        <path d="M21 12a9 9 0 1 1-9-9" />
        <path d="M22 2L18 6" />
        <path d="M22 6h-4V2" />
      </svg>
    ),
    title: "24/7 System Reliability",
    desc: "Travel businesses operate around the clock. Your IT systems need to match — with proactive monitoring and rapid response.",
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <path d="M9 12l2 2 4-4" />
      </svg>
    ),
    title: "Secure Client Booking Data",
    desc: "Client passport details, payment information, and booking data require enterprise-grade security and compliance.",
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" />
      </svg>
    ),
    title: "Cloud Collaboration",
    desc: "Teams across multiple offices need seamless access to shared files, email, and communication tools.",
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.362 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
      </svg>
    ),
    title: "Communications",
    desc: "Reliable phone systems and unified communications that keep your team connected regardless of location.",
  },
];

const services = [
  { title: "Managed IT Services", href: "/managed-it-services", desc: "Proactive IT management and 24/7 monitoring for travel businesses." },
  { title: "Multi-Site Infrastructure", href: "/it-infrastructure", desc: "Consistent technology deployment across all your office locations." },
  { title: "Cloud & Microsoft 365", href: "/cloud-solutions/microsoft-365", desc: "Cloud collaboration tools for distributed travel teams." },
  { title: "Business Communications", href: "/business-communications", desc: "Unified communications and cloud phone systems." },
  { title: "Cybersecurity", href: "/cyber-security", desc: "Protect client data and booking systems from cyber threats." },
  { title: "Network Solutions", href: "/network-solutions", desc: "Reliable network infrastructure across all your locations." },
];

export default function TravelPage() {
  return (
    <PageTransition>
      <main style={{ background: "#070B14", overflowX: "hidden" }}>
        <Header />
        <Breadcrumb
          items={[
            { label: "Home", href: "/" },
            { label: "Industries", href: "/industries" },
            { label: "Travel" },
          ]}
        />
        <section className="relative min-h-[320px] md:min-h-[480px] flex items-center justify-center py-32 overflow-hidden">
          <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, #070B14 0%, #0B1220 40%, #0f1f3d 70%, #070B14 100%)" }} />
          <div className="absolute top-1/3 right-1/4 w-96 h-96 rounded-full blob-1" style={{ background: "radial-gradient(circle, rgba(37,99,235,0.15) 0%, transparent 70%)" }} />
          <div className="relative max-w-container mx-auto px-4 sm:px-6 text-center">
            <FadeInView delay={0}>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-pill mb-8" style={{ background: "rgba(37,99,235,0.12)", border: "1px solid rgba(59,130,246,0.25)" }}>
                <span className="mr-1" style={{ color: "#60A5FA" }}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M17.8 19.2L16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z" /></svg></span>
                <span className="text-[11px] font-body font-600 uppercase tracking-widest gradient-text">Travel Industry</span>
              </div>
            </FadeInView>
            <FadeInView delay={0.15}>
              <h1 className="font-heading font-800 text-display-md md:text-display mb-6" style={{ color: "#F8FAFC", letterSpacing: "-0.03em", lineHeight: "1.05" }}>
                Technology Infrastructure for<br />
                <span className="gradient-text">Travel Agencies and Tour Operators</span>
              </h1>
            </FadeInView>
            <FadeInView delay={0.3}>
              <p className="text-body-lg leading-[1.75] max-w-3xl mx-auto mb-10" style={{ color: "#94A3B8" }}>
                Pro IT Solutions understands the technology demands of travel businesses — distributed offices, 24/7 reliability, secure client data, and seamless team collaboration.
              </p>
            </FadeInView>
            <FadeInView delay={0.45}>
              <Link href="/book" className="px-6 sm:px-8 py-3.5 sm:py-4 text-[15px] font-heading font-600 rounded-pill btn-primary">Book a Travel IT Consultation</Link>
            </FadeInView>
          </div>
        </section>

        {/* Challenges */}
        <section className="py-20 lg:py-28" style={{ background: "#0B1220" }}>
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <FadeInView className="text-center mb-16">
              <h2 className="font-heading font-700 text-[24px] sm:text-[36px] leading-[1.05] mb-4" style={{ color: "#F8FAFC" }}>Travel IT Challenges We Solve</h2>
            </FadeInView>
            <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch" staggerDelay={0.08}>
              {challenges?.map((c) => (
                <div key={c?.title} className="glass-card rounded-card p-6 h-full flex flex-col">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: "rgba(59,130,246,0.12)", color: "#60A5FA" }}>{c?.icon}</div>
                  <h3 className="font-heading font-700 text-[24px] mb-3" style={{ color: "#F8FAFC" }}>{c?.title}</h3>
                  <p className="text-body-sm leading-[1.75]" style={{ color: "#94A3B8" }}>{c?.desc}</p>
                </div>
              ))}
            </StaggerContainer>
          </div>
        </section>

        {/* Services */}
        <section className="py-20">
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <FadeInView className="text-center mb-16">
              <h2 className="font-heading font-700 text-[24px] sm:text-[36px] leading-[1.05] mb-4" style={{ color: "#F8FAFC" }}>Services for Travel Businesses</h2>
            </FadeInView>
            <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch" staggerDelay={0.08}>
              {services?.map((svc) => (
                <Link key={svc?.title} href={svc?.href} className="group rounded-card-lg p-6 transition-all duration-300 hover:scale-[1.02] h-full flex flex-col" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
                  <h3 className="font-heading font-700 text-[24px] mb-2" style={{ color: "#F8FAFC" }}>{svc?.title}</h3>
                  <p className="text-[14px] font-body mb-4 leading-[1.75]" style={{ color: "#94A3B8" }}>{svc?.desc}</p>
                  <span className="text-[14px] font-body font-medium mt-auto" style={{ color: "#60A5FA" }}>Learn more →</span>
                </Link>
              ))}
            </StaggerContainer>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20">
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <FadeInView scale={0.95}>
              <div className="rounded-card-lg p-6 sm:p-10 lg:p-16 text-center" style={{ background: "rgba(37,99,235,0.08)", border: "1px solid rgba(59,130,246,0.2)" }}>
                <h2 className="font-heading font-700 text-[24px] sm:text-[36px] leading-[1.05] mb-4" style={{ color: "#F8FAFC" }}>Ready to improve your travel agency IT?</h2>
                <p className="text-body-lg mb-8 leading-[1.75]" style={{ color: "#94A3B8" }}>Let&apos;s discuss your technology requirements and build a reliable IT foundation for your business.</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link href="/book" className="px-6 sm:px-8 py-3.5 sm:py-4 rounded-pill font-heading font-600 btn-primary">Book a Free Consultation</Link>
                  <Link href="/services" className="px-6 sm:px-8 py-3.5 sm:py-4 rounded-pill font-heading font-500 btn-secondary">View All Services</Link>
                </div>
              </div>
            </FadeInView>
          </div>
        </section>

        <Footer />
      </main>
    </PageTransition>
  );
}
